import { useState, useEffect } from "react"
import { Link } from "react-router-dom"
import { FileText, Clock, Trash2, Edit, Loader2 } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog"
import { useAuth } from "@/context/AuthContext"
import { useToast } from "@/hooks/useToast"
import { getPosts, deletePost, isNetworkFetchError } from "@/lib/db"
import { formatRelativeTime } from "@/lib/utils"
import type { Post } from "@/types"

export default function DraftsPage() {
  const { user, firebaseUser } = useAuth()
  const { toast } = useToast()
  const [drafts, setDrafts] = useState<Post[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    async function loadDrafts() {
      const authorId = user?.uid || firebaseUser?.id
      if (!authorId) {
        setLoading(false)
        return
      }

      try {
        const result = await getPosts({
          authorId,
          status: "draft",
          orderByField: "updated_at",
          orderDirection: "desc",
        })
        setDrafts(result.posts)
      } catch (error) {
        if (!isNetworkFetchError(error)) {
          console.error("Failed to load drafts:", error)
        }
      } finally {
        setLoading(false)
      }
    }

    loadDrafts()
  }, [user, firebaseUser])

  const handleDelete = async (id: string) => {
    try {
      await deletePost(id)
      setDrafts(prev => prev.filter(draft => draft.id !== id))
      toast({ title: "Post deleted" })
    } catch (error) {
      if (!isNetworkFetchError(error)) {
        console.error("Failed to delete draft:", error)
      }
      toast({ title: "Failed to delete draft", variant: "destructive" })
    }
  }

  if (loading) {
    return (
      <div className="container mx-auto flex max-w-4xl items-center justify-center px-4 py-16">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    )
  }

  return (
    <div className="container mx-auto max-w-4xl px-4 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-foreground">My Drafts</h1>
        <p className="mt-2 text-muted-foreground">Continue working on your unpublished posts</p>
      </div>

      {drafts.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16">
            <FileText className="mb-4 h-12 w-12 text-muted-foreground" />
            <h3 className="mb-2 text-lg font-semibold text-foreground">No drafts yet</h3>
            <p className="mb-4 text-sm text-muted-foreground">Start writing and save as draft to see them here</p>
            <Link to="/write">
              <Button>Create New Post</Button>
            </Link>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {drafts.map(draft => (
            <Card key={draft.id} className="hover:border-primary/50 transition-colors">
              <CardHeader>
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <CardTitle className="text-xl">{draft.title || "Untitled Draft"}</CardTitle>
                    {draft.subtitle && <CardDescription className="mt-1">{draft.subtitle}</CardDescription>}
                    <div className="mt-2 flex items-center gap-4 text-sm text-muted-foreground">
                      <div className="flex items-center gap-1">
                        <Clock className="h-4 w-4" />
                        <span>Last edited {formatRelativeTime(draft.updatedAt)}</span>
                      </div>
                      {draft.readingTime > 0 && <span>{draft.readingTime} min read</span>}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Link to={`/edit/${draft.id}`}>
                      <Button variant="outline" size="sm">
                        <Edit className="mr-2 h-4 w-4" />
                        Edit
                      </Button>
                    </Link>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="outline" size="sm">
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Delete draft?</AlertDialogTitle>
                          <AlertDialogDescription>
                            This action cannot be undone. This will permanently delete your draft.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction onClick={() => handleDelete(draft.id)} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                            Delete
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </div>
              </CardHeader>
              {draft.excerpt && (
                <CardContent>
                  <p className="text-sm text-muted-foreground line-clamp-2">{draft.excerpt}</p>
                </CardContent>
              )}
            </Card>
          ))}
        </div>
      )}
    </div>
  )
}
